import { type FC, useMemo } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import type { UsageTimeseriesBucket } from "../../types/usage";

import { useTheme } from "../../context/ThemeContext";
import { getChartTheme } from "../../theme/chartTheme";
import { formatTokenCount } from "../../utils/currency";
import { toDate } from "../../utils/time";
import { ContentCard } from "../common/ContentCard";
import { SectionHeader } from "../common/SectionHeader";
import { Text } from "../common/Text";

export interface UsageTimeseriesStackedChartProps {
  buckets: UsageTimeseriesBucket[] | undefined;
}

/* ------------------------------------------------------------------ */
/*  Data                                                               */
/* ------------------------------------------------------------------ */

interface StackedRow {
  timestamp: number;
  prompt: number;
  completion: number;
  cached: number;
  cacheWrite: number;
}

function buildStackedRows(buckets: UsageTimeseriesBucket[]): StackedRow[] {
  return buckets.map((bucket) => ({
    cacheWrite: bucket.totals.cacheWriteTokens,
    cached: bucket.totals.cachedInputTokens,
    completion: bucket.totals.completionTokens,
    prompt: bucket.totals.promptTokens,
    timestamp: bucket.startTimestampMs,
  }));
}

/* ------------------------------------------------------------------ */
/*  Formatters                                                         */
/* ------------------------------------------------------------------ */

function formatTickDate(ts: number): string {
  const d = toDate(ts);
  if (!d) {
    return "";
  }
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

/* ------------------------------------------------------------------ */
/*  Tooltip                                                            */
/* ------------------------------------------------------------------ */

interface StackedTooltipPayloadEntry {
  name: string;
  value: number;
  color: string;
}

interface StackedTooltipProps {
  active?: boolean;
  payload?: StackedTooltipPayloadEntry[];
  label?: number;
  isDark: boolean;
}

const StackedTooltip: FC<StackedTooltipProps> = ({
  active,
  payload,
  label,
  isDark,
}) => {
  if (!active || !payload || payload.length === 0) {
    return null;
  }
  const date = toDate(label ?? null);

  return (
    <div
      className="rounded-none border px-2.5 py-1.5 text-xs shadow-sm"
      style={{
        backgroundColor: getChartTheme(isDark).tooltipBg,
        borderColor: getChartTheme(isDark).tooltipBorder,
        color: getChartTheme(isDark).tooltipText,
      }}
    >
      {date && (
        <div
          className="mb-1"
          style={{ color: getChartTheme(isDark).tooltipMuted }}
        >
          {date.toLocaleDateString(undefined, {
            day: "numeric",
            month: "short",
            year: "numeric",
          })}
        </div>
      )}
      {payload
        .slice()
        .toReversed()
        .map((entry) => (
          <div key={entry.name} className="flex items-center gap-1.5">
            <div
              className="h-2 w-2 rounded-full"
              style={{ backgroundColor: entry.color }}
            />
            <span>
              {entry.name}:{" "}
              <span className="font-semibold">
                {formatTokenCount(entry.value)}
              </span>
            </span>
          </div>
        ))}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

const AXIS_TICK_STYLE = { fontSize: 10 };

export const UsageTimeseriesStackedChart: FC<
  UsageTimeseriesStackedChartProps
> = ({ buckets }) => {
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const data = useMemo(
    () => (buckets && buckets.length > 0 ? buildStackedRows(buckets) : []),
    [buckets],
  );

  if (data.length === 0) {
    return null;
  }

  const { axisText, gridLine } = getChartTheme(isDark);
  const cursorLine = isDark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.10)";

  const promptStroke = isDark
    ? "rgba(148, 163, 184, 0.80)"
    : "rgba(71, 85, 105, 0.80)";
  const promptFill = isDark
    ? "rgba(148, 163, 184, 0.25)"
    : "rgba(71, 85, 105, 0.20)";
  const completionStroke = isDark
    ? "rgba(52, 211, 153, 0.80)"
    : "rgba(16, 185, 129, 0.80)";
  const completionFill = isDark
    ? "rgba(52, 211, 153, 0.25)"
    : "rgba(16, 185, 129, 0.20)";
  const cachedStroke = isDark
    ? "rgba(251, 113, 133, 0.80)"
    : "rgba(225, 29, 72, 0.80)";
  const cachedFill = isDark
    ? "rgba(251, 113, 133, 0.25)"
    : "rgba(225, 29, 72, 0.20)";
  const cacheWriteStroke = isDark
    ? "rgba(245, 158, 11, 0.80)"
    : "rgba(217, 119, 6, 0.80)";
  const cacheWriteFill = isDark
    ? "rgba(245, 158, 11, 0.25)"
    : "rgba(217, 119, 6, 0.20)";

  return (
    <div className="space-y-3">
      <SectionHeader title="Token Breakdown Over Time" />
      <ContentCard className="space-y-3">
        <Text variant="label" as="div">
          Stacked token types
        </Text>
        <ResponsiveContainer width="100%" height={200}>
          <AreaChart
            data={data}
            margin={{ bottom: 0, left: -16, right: 4, top: 4 }}
          >
            <CartesianGrid vertical={false} stroke={gridLine} />
            <XAxis
              dataKey="timestamp"
              tick={AXIS_TICK_STYLE}
              stroke={axisText}
              tickLine={false}
              axisLine={false}
              tickFormatter={formatTickDate}
            />
            <YAxis
              tick={AXIS_TICK_STYLE}
              stroke={axisText}
              tickLine={false}
              axisLine={false}
              tickFormatter={(v: number) => formatTokenCount(v)}
            />
            <Tooltip
              cursor={{ stroke: cursorLine }}
              content={<StackedTooltip isDark={isDark} />}
            />
            <Legend
              iconType="rect"
              iconSize={8}
              wrapperStyle={{ color: axisText, fontSize: 11 }}
            />
            <Area
              type="monotone"
              name="Prompt"
              dataKey="prompt"
              stackId="tokens"
              stroke={promptStroke}
              fill={promptFill}
              strokeWidth={1.5}
            />
            <Area
              type="monotone"
              name="Completion"
              dataKey="completion"
              stackId="tokens"
              stroke={completionStroke}
              fill={completionFill}
              strokeWidth={1.5}
            />
            <Area
              type="monotone"
              name="Cached"
              dataKey="cached"
              stackId="tokens"
              stroke={cachedStroke}
              fill={cachedFill}
              strokeWidth={1.5}
            />
            <Area
              type="monotone"
              name="Cache write"
              dataKey="cacheWrite"
              stackId="tokens"
              stroke={cacheWriteStroke}
              fill={cacheWriteFill}
              strokeWidth={1.5}
            />
          </AreaChart>
        </ResponsiveContainer>
      </ContentCard>
    </div>
  );
};
